import { useTenantMemberships } from "@/contexts/TenantContext";
import { Colors } from "@/enums/Colors";
import { DataRoomEntry } from "@/pages/OneDataRoomPage";
import DataRoomService from "@/services/DataRoomService";
import { ExpandMore } from "@mui/icons-material";
import { Box, MenuItem, Select, SelectProps, styled, Typography, typographyClasses } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SimpleStarIcon } from "../utils/icons/SimpleStarIcon";
import { CommonStyledSelectMenuProps } from "../utils/SelectStyles";
import { useDataRoomProvider } from "./contexts/DataRoomContext";
import { SimpleButtonContainer } from "./DataRoomAppBar";
import { MASTER_ENTRY } from "./DataRoomCategoriesDefines";
import { DataRoomCustomFoldersTree } from "./DataRoomCustomFoldersTree";
import { DataRoomRecursiveList } from "./DataRoomRecursiveList";

export enum TREE_FILTERS {
  COMMON = "Common",
  ACTIVE = "Active",
  CUSTOM = "Custom",
}

interface DataRoomDirectoryFileTreeProps {
  hoverColor?: string;
  tree: DataRoomEntry;
  setTree: (t: DataRoomEntry) => void;
  onNodeClick?: () => void;
}

/**
 * Only visually a tree its really multiple recursive lists
 */
export default function DataRoomDirectoryFileTree(props: DataRoomDirectoryFileTreeProps) {
  const { t } = useTranslation("virtualDataRooms");
  const [filteredTree, setFilteredTree] = useState<DataRoomEntry>(null);

  const { selected, setSelected, selectedCustom, setSelectedCustom, closeAll, setCloseAll, treeFilter, setTreeFilter } =
    useDataRoomProvider();

  const { currentTenantMembership } = useTenantMemberships();

  useEffect(() => {
    let clone = structuredClone(props.tree);
    let stack = [clone];
    while (stack.length > 0) {
      let e = stack.pop();
      if (e.entries) {
        e.entries = e.entries.filter((i) => i.count > 0);
        for (let newI of e.entries) {
          stack.push(newI);
        }
      }
    }
    setFilteredTree(clone);
  }, [props.tree]);

  useEffect(() => {
    DataRoomService.getStats(currentTenantMembership.tenant.externalId, props.tree).then((r) => r.onSuccess((t) => props.setTree(t)));
  }, [currentTenantMembership.tenant.externalId]);

  const isOnMasterEntry = treeFilter !== TREE_FILTERS.CUSTOM ? selected?.staticId === MASTER_ENTRY.staticId : !selectedCustom;

  function onClickProcessing() {
    setSelected(MASTER_ENTRY);
    setSelectedCustom(null);
  }

  return (
    <>
      <RootContainer>
        <Header>
          <CommonStyledSelect<TREE_FILTERS>
            value={treeFilter}
            renderValue={(v) => `${t("directoryTree.filter.show")}: ${t(`directoryTree.filter.${v.toLowerCase()}`)}`}
            onChange={(v) => setTreeFilter(v.target.value as TREE_FILTERS)}
            MenuProps={CommonStyledSelectMenuProps}
          >
            <MenuItem value={TREE_FILTERS.COMMON}>
              <Typography fontSize="0.75rem">{t("directoryTree.filter.common")}</Typography>
            </MenuItem>
            <MenuItem value={TREE_FILTERS.ACTIVE}>
              <Typography fontSize="0.75rem">{t("directoryTree.filter.active")}</Typography>
            </MenuItem>
            <MenuItem value={TREE_FILTERS.CUSTOM}>
              <Typography fontSize="0.75rem">{t("directoryTree.filter.custom")}</Typography>
            </MenuItem>
          </CommonStyledSelect>
          <SimpleButtonContainer onClick={() => setCloseAll(true)}>
            <Typography>{t("directoryTree.closeAll")}</Typography>
          </SimpleButtonContainer>
        </Header>
        <Footer>
          <Box
            onClick={onClickProcessing}
            sx={{
              backgroundColor: isOnMasterEntry && Colors.BUTTON_SECONDARY_HOVER_PURPLE,
            }}
          >
            <SimpleStarIcon style={{ fill: `${Colors.LIGHT_GRAY_8A8E93}` }} />
            <Typography>{t("directoryTree.processing")}</Typography>
          </Box>
        </Footer>
        <TreeContainer>
          {treeFilter === TREE_FILTERS.COMMON &&
            props.tree?.entries?.map((e) => (
              <DataRoomRecursiveList
                closeAll={closeAll}
                setCloseAll={setCloseAll}
                level={0}
                key={e.staticId}
                entry={e}
                hoverColor={props.hoverColor}
                setSelected={setSelected}
                selected={selected}
                onNodeClick={props.onNodeClick}
              />
            ))}
          {treeFilter === TREE_FILTERS.ACTIVE &&
            filteredTree?.entries?.map((e) => (
              <DataRoomRecursiveList
                closeAll={closeAll}
                setCloseAll={setCloseAll}
                level={0}
                key={e.staticId}
                entry={e}
                hoverColor={props.hoverColor}
                setSelected={setSelected}
                selected={selected}
                onNodeClick={props.onNodeClick}
              />
            ))}
          {treeFilter === TREE_FILTERS.CUSTOM && (
            <DataRoomCustomFoldersTree level={0} setSelected={setSelectedCustom} selected={selectedCustom} />
          )}
        </TreeContainer>
      </RootContainer>
    </>
  );
}

const RootContainer = styled(Box)({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  maxWidth: "100%",
  overflow: "hidden",
  height: "100%",
  maxHeight: "100%",
});

const Header = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0.5rem 1rem",
  borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  [`& .${typographyClasses.root}`]: {
    display: "flex",
    padding: "0.25rem 0",
    alignItems: "center",
    fontSize: "0.75rem",
    fontWeight: 400,
    lineHeight: "18px",
  },
});

const TreeContainer = styled(Box)(({ theme }) => ({
  padding: "0 1rem",
  width: "100%",
  overflowY: "auto",
  flex: 1,
  minHeight: 0,
}));

const Footer = styled(Box)({
  flexShrink: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "center",
  width: "100%",
  padding: "0.5rem 1rem",
  borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  "& > div": {
    width: "100%",
    display: "flex",
    alignItems: "center",
    columnGap: "0.5625rem",
    borderRadius: "0.5rem",
    paddingLeft: "1.9375rem",
    ":hover": {
      cursor: "pointer",
      backgroundColor: Colors.LIGHT_GRAY_DEDEE1,
    },
  },
  [`& .${typographyClasses.root}`]: {
    fontSize: "0.875rem",
    fontWeight: 400,
    lineHeight: "19.6px",
    gap: "0.5rem",
    display: "flex",
    alignItems: "center",
    height: "34px",
    width: "100%",
  },
});

export function CommonStyledSelect<T extends string>(props: SelectProps<T>) {
  return (
    <Select
      IconComponent={ExpandMore}
      sx={{
        fontSize: "0.75rem",
        fontWeight: "400",
        height: "2.2rem",
        color: `${Colors.BLACK_151517}`,
        borderRadius: "0.500rem",
        "& .MuiOutlinedInput-notchedOutline": {
          border: 0,
        },
        "&.Mui-focused": {
          border: 0,
          "& .MuiOutlinedInput-notchedOutline": {
            borderWidth: "1px",
          },
        },
      }}
      {...props}
    />
  );
}
